import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form940vsForm941 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Understand the Difference between Form 940 and 941"
          description="Learn the key differences between Form 940 and Form 941 (FUTA vs payroll tax), including tax rates, filing deadlines, and deposit requirements for employers."
          keywords="e file 941, IRS 941 Form, Form 940, FUTA tax "
          canonical="resources/employment-tax/form-940-vs-form-941/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Understanding the Difference between
          <br />{' '}
          <span className="highlight-container">
            <span className="highlight">Form 940</span>
          </span>{' '}
          and{' '}
          <span className="highlight-container">
            <span className="highlight">Form 941</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn the key differences between Form 940 and Form 941, including
          what taxes they report, <br />
          filing frequency, and employer responsibilities.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form-940-vs-form-941`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-940-vs-form-941/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 940 vs. Form 941 at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 940</b>
                        </Link>
                        {' is an annual return used by employers to report '}
                        <b>Federal Unemployment Tax Act (FUTA)</b>
                        {' taxes, while '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 941</b>
                        </Link>
                        {' is a quarterly return used to report '}
                        <b>
                          federal income tax withheld, Social Security tax, and
                          Medicare tax (FICA)
                        </b>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Form 940:</b>
                        {' Employers generally '}
                        <Link
                          href="https://www.taxzerone.com/employment-tax/e-file-irs-form-940/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form 940
                        </Link>{' '}
                        <b>annually</b>
                        {
                          ' to report FUTA wages, calculate their federal unemployment tax liability, and claim applicable state unemployment tax credits. The FUTA tax generally applies to the first '
                        }
                        <b>$7,000 of wages</b>
                        {' paid to each employee.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Form 941:</b>
                        {' Most employers '}
                        <Link
                          href="https://www.taxzerone.com/employment-tax/e-file-irs-form-941/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form 941
                        </Link>{' '}
                        <b>quarterly</b> to report
                        {
                          ' wages paid, federal income tax withheld, and employer and employee shares of Social Security and Medicare taxes. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Key Difference:</b>
                        {' Form 940 covers '}
                        <b>federal unemployment taxes paid by the employer</b>
                        {', while Form 941 covers '}
                        <b>
                          federal income tax withholding and Social Security and
                          Medicare taxes
                        </b>
                        {' associated with employee wages.'}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Employers in the United States must comply with multiple
                      federal payroll tax obligations, and two of the most
                      important filings are{' '}
                      <Link
                        href="/resources/employment-tax/form-940-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 940
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941
                      </Link>
                      . Understanding the differences between these forms is
                      essential for accurate IRS reporting.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>

                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form-940" sx={{ color: '#0202ff' }}>
                          What is Form 940?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form-941" sx={{ color: '#0202ff' }}>
                          What is Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#difference" sx={{ color: '#0202ff' }}>
                          Difference Between Form 940 and Form 941
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText id="form-940">
                        <Link href="#efile" sx={{ color: '#0202ff' }}>
                          E-File your Payroll Forms with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  What is Form 940?
                </Typography>

                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    Form 940, Employer's Annual Federal Unemployment Tax Return,
                    is an IRS form used by employers to report and pay Federal
                    Unemployment Tax Act (
                    <Link
                      href="/resources/employment-tax/futa-tax/"
                      sx={{ color: '#0202ff' }}
                    >
                      FUTA
                    </Link>
                    ) taxes.
                  </li>
                  <li>
                    These taxes help fund unemployment compensation programs
                    that provide benefits to workers who lose their jobs, not on
                    their own.
                  </li>
                  <li id="form-941">
                    Employers who meet the IRS filing requirements must{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-940/"
                      sx={{ color: '#0202ff' }}
                    >
                      file Form 940
                    </Link>{' '}
                    annually to calculate their annual FUTA tax liability,
                    report taxable wages paid to employees and claim any
                    applicable credits for state unemployment taxes paid.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  What is Form 941?
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    Form 941,{' '}
                    <Link
                      href="/resources/employment-tax/employers-quarterly-federal-tax-return/"
                      sx={{ color: '#0202ff' }}
                    >
                      Employer's Quarterly Federal Tax Return
                    </Link>
                    , is used by employers to report federal income tax withheld
                    from employees wages, as well as the employers and employees
                    shares of Social Security and Medicare taxes (
                    <Link
                      href="/resources/employment-tax/fica-tax/"
                      sx={{ color: '#0202ff' }}
                    >
                      FICA
                    </Link>
                    ).
                  </li>
                  <li>
                    Most employers are required to{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      file Form 941
                    </Link>{' '}
                    every quarter to report wages paid, tips received, taxes
                    withheld, and payroll tax liabilities. The form helps the
                    IRS track and reconcile payroll taxes throughout the year.
                  </li>
                  <li>
                    Even if you paid no wages during a quarter, you must file a{' '}
                    <Link
                      target="_blank"
                      href="https://support.taxzerone.com/how-to-file-a-zero-tax-return-for-form-941-using-taxzerone/"
                      sx={{ color: '#0202ff' }}
                    >
                      Zero Return
                    </Link>{' '}
                    to remain compliant with the IRS.
                  </li>
                </Box>
                <Alert
                  id="difference"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4 }}
                >
                  <b>Note:</b> Whether you're a{' '}
                  <span style={{ fontWeight: 600 }}>
                    CPA, enrolled agent, or other tax professional
                  </span>
                  , TaxZerone helps you simplify Form 941 filing and manage
                  returns efficiently.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Difference Between Form 940 and Form 941
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The key differences between 940 and 941 is explained:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Form 940 and Form 941 Comparison Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 250 }}>
                            Feature
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 940
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 941
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Tax reported
                          </TableCell>

                          <TableCell>
                            Federal Unemployment Tax Act (FUTA)
                          </TableCell>

                          <TableCell>
                            Federal income tax withheld, Social Security tax,
                            Medicare tax (FICA)
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Filing frequency
                          </TableCell>

                          <TableCell>Annual</TableCell>

                          <TableCell>Quarterly</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Filing deadline
                          </TableCell>

                          <TableCell>January 31</TableCell>

                          <TableCell>
                            <Box
                              component="ul"
                              sx={{
                                mb: 0,
                                mt: 0,
                                ml: 0,
                                pl: 3,
                                '& ul, li': { mb: 1.5 },
                              }}
                            >
                              <li>
                                <span style={{ fontWeight: 600 }}>Q1:</span>{' '}
                                April 30
                              </li>
                              <li>
                                <span style={{ fontWeight: 600 }}>Q2:</span>{' '}
                                July 31
                              </li>
                              <li>
                                <span style={{ fontWeight: 600 }}>Q3:</span>{' '}
                                October 31
                              </li>
                              <li>
                                <span style={{ fontWeight: 600 }}>Q4:</span>{' '}
                                January 31
                              </li>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who pays it
                          </TableCell>

                          <TableCell>Employer only</TableCell>

                          <TableCell>Employer and employee</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Tax rate
                          </TableCell>

                          <TableCell>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <span style={{ fontWeight: 600 }}>
                                FUTA Tax Rate:
                              </span>{' '}
                              6.0%
                            </Typography>

                            <Alert
                              severity="warning"
                              icon={false}
                              sx={{
                                mb: 3,
                                fontSize: '12px',
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>Notes:</span> If
                              employers pay SUTA tax on time, they may receive a
                              credit of{' '}
                              <span style={{ fontWeight: 600 }}>5.4%</span> and
                              the net effective FUTA rate is{' '}
                              <span style={{ fontWeight: 600 }}>0.6%</span>.
                            </Alert>
                          </TableCell>

                          <TableCell>
                            <Box
                              component="ul"
                              sx={{
                                mb: 0,
                                mt: 0,
                                ml: 0,
                                pl: 3,
                                '& ul, li': { mb: 1.5 },
                              }}
                            >
                              <li>
                                <span style={{ fontWeight: 600 }}>
                                  Social Security Tax: 12.4%
                                </span>{' '}
                                (6.2% for employee & employer)
                              </li>

                              <li>
                                <span style={{ fontWeight: 600 }}>
                                  Medicare Tax: 2.9%
                                </span>{' '}
                                (1.45% for employee & employer)
                              </li>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Taxable wage base
                          </TableCell>

                          <TableCell>
                            The first{' '}
                            <span style={{ fontWeight: 600 }}>$7,000</span> of
                            wages paid to each employee per year is subject to
                            FUTA tax.
                          </TableCell>

                          <TableCell>
                            <Box
                              component="ul"
                              sx={{
                                mb: 0,
                                mt: 0,
                                ml: 0,
                                pl: 3,
                                '& ul, li': { mb: 1.5 },
                              }}
                            >
                              <li>
                                Social Security tax applies up to the annual
                                wage base limit of{' '}
                                <span style={{ fontWeight: 600 }}>
                                  $184,500 for 2026
                                </span>
                                .
                              </li>

                              <li>
                                Medicare tax applies to{' '}
                                <span style={{ fontWeight: 600 }}>
                                  all wages
                                </span>{' '}
                                with no wage limit.
                              </li>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Deposits
                          </TableCell>

                          <TableCell>
                            <Box component="div" sx={{ mb: 1 }}>
                              If your FUTA tax liability is
                            </Box>

                            <Box
                              component="ul"
                              sx={{
                                mb: 0,
                                mt: 0,
                                ml: 0,
                                pl: 3,
                                '& ul, li': { mb: 1.5 },
                              }}
                            >
                              <li>
                                <span style={{ fontWeight: 600 }}>
                                  More than $500
                                </span>{' '}
                                in a quarter, deposit by the last day of the
                                following month.
                              </li>

                              <li>
                                <span style={{ fontWeight: 600 }}>
                                  $500 or less
                                </span>{' '}
                                in a quarter: carry it forward to the next
                                quarter.
                              </li>

                              <li>
                                If fourth-quarter liability is{' '}
                                <span style={{ fontWeight: 600 }}>
                                  $500 or less
                                </span>
                                , you can pay it with your Form 940 filing
                                before the deadline.
                              </li>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box
                              component="ul"
                              sx={{
                                mb: 0,
                                mt: 0,
                                ml: 0,
                                pl: 3,
                                '& ul, li': { mb: 1.5 },
                              }}
                            >
                              <li>
                                If your total tax liability during the lookback
                                period is
                              </li>
                              <Box
                                id="filing"
                                component="ul"
                                sx={{ mt: 1, '& li': { mb: 0.5 } }}
                              >
                                <li>
                                  <span style={{ fontWeight: 600 }}>
                                    $50,000 or less:
                                  </span>{' '}
                                  Monthly
                                </li>
                                <li>
                                  <span style={{ fontWeight: 600 }}>
                                    More than $50,000:
                                  </span>{' '}
                                  Semiweekly
                                </li>
                              </Box>

                              <li>
                                If you accumulate{' '}
                                <span style={{ fontWeight: 600 }}>
                                  $100,000 or more
                                </span>{' '}
                                in tax liability on any day, you must deposit it
                                by the next business day.
                              </li>

                              <li id="efile">
                                If your total tax liability for the quarter is{' '}
                                <span style={{ fontWeight: 600 }}>
                                  less than $2,500
                                </span>
                                , you can{' '}
                                <span style={{ fontWeight: 600 }}>
                                  pay the amount with your Form 941 filing.
                                </span>
                              </li>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-File your Form 940 and Form 941 with TaxZerone
                </Typography>

                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 3 }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      <Link
                        target="_blank"
                        href="https://support.taxzerone.com/how-to-file-a-zero-tax-return-for-form-941-using-taxzerone/"
                        sx={{ color: '#0202ff' }}
                      >
                        Report zero wages
                      </Link>{' '}
                      on Form 941-
                    </span>{' '}
                    Even if you have no payroll activity for the quarter.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/resources/employment-tax/form-941-bulk-upload/"
                        sx={{ color: '#0202ff' }}
                      >
                        941 Bulk upload
                      </Link>{' '}
                      for multiple filers -
                    </span>{' '}
                    Upload multiple Form 941 returns at once using our bulk
                    upload feature, ideal for{' '}
                    <Link href="/tax-professionals/" sx={{ color: '#0202ff' }}>
                      tax professionals
                    </Link>{' '}
                    and businesses managing high-volume filings.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      All schedules included at no additional cost -
                    </span>{' '}
                    TaxZerone supports{' '}
                    <Link
                      href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                      sx={{ color: '#0202ff' }}
                    >
                      941 Schedule B
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/employment-tax/form-8974-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8974
                    </Link>
                    , Form 8453-EMP, and{' '}
                    <Link
                      href="/resources/employment-tax/form-940-schedule-a/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 940 Schedule A
                    </Link>{' '}
                    - all included without fees.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Flexible payment options -
                    </span>{' '}
                    Pay securely via{' '}
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      EFW
                    </Link>
                    ,{' '}
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      EFTPS
                    </Link>
                    , credit or debit card, or money order.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>Expert support -</span>{' '}
                    Get assistance from our friendly support team via Phone
                    (English and Spanish), Live chat or Email.
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source & Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the
                        Instructions for{' '}
                        <Link
                          target="_blank"
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Form 941 and{' '}
                        <Link
                          target="_blank"
                          href="https://www.irs.gov/pub/irs-pdf/i940.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Form 940,{' '}
                        <Link
                          target="_blank"
                          href="https://www.irs.gov/taxtopics/tc759"
                          sx={{ color: '#0202ff' }}
                        >
                          Tc759.
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file your FUTA and Payroll forms with TaxZerone?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-File Form 941 and Form 940 with TaxZerone and get accepted in a
              few minutes.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form-940-vs-form-941`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form940vsForm941;
